import { createSlice } from '@reduxjs/toolkit';

// Load flagged items from localStorage
const loadFlaggedItems = () => {
  try {
    const serializedState = localStorage.getItem('flaggedItems');
    if (serializedState === null) {
      return { certificates: [], documents: [], supplementalDocuments: [] };
    }
    const state = JSON.parse(serializedState);
    // Ensure supplementalDocuments array exists for backward compatibility
    if (!state.supplementalDocuments) {
      state.supplementalDocuments = [];
    }
    return state;
  } catch (err) {
    console.error('Error loading flagged items:', err);
    return { certificates: [], documents: [], supplementalDocuments: [] };
  }
};

// Save flagged items to localStorage
const saveFlaggedItems = (state) => {
  try {
    const serializedState = JSON.stringify(state);
    localStorage.setItem('flaggedItems', serializedState);
  } catch (err) {
    console.error('Error saving flagged items:', err);
  }
};

const initialState = loadFlaggedItems();

const flaggedItemsSlice = createSlice({
  name: 'flaggedItems',
  initialState,
  reducers: {
    flagCertificate: (state, action) => {
      const { certificate, flaggedAt = new Date().toISOString(), flaggedBy } = action.payload;
      
      // Check if already flagged
      const existingIndex = state.certificates.findIndex(item => item.id === certificate.id);
      if (existingIndex === -1) {
        state.certificates.push({
          id: certificate.id,
          type: 'certificate',
          name: certificate.name || certificate.insuredName || 'Unnamed Certificate',
          insured: certificate.insured || certificate.insuredName,
          vendor: certificate.vendor,
          vendorName: certificate.vendorName,
          clientVendorID: certificate.clientVendorID,
          status: certificate.status,
          certificateDate: certificate.certificateDate,
          firstExp: certificate.firstExp,
          flaggedAt,
          flaggedBy
        });
        saveFlaggedItems(state);
      }
    },
    
    unflagCertificate: (state, action) => {
      const certificateId = action.payload;
      state.certificates = state.certificates.filter(item => item.id !== certificateId);
      saveFlaggedItems(state);
    },
    
    flagDocument: (state, action) => {
      const { document, flaggedAt = new Date().toISOString(), flaggedBy } = action.payload;
      
      // Check if already flagged
      const existingIndex = state.documents.findIndex(item => item.id === document.id);
      if (existingIndex === -1) {
        state.documents.push({
          id: document.id,
          type: 'document',
          name: document.name || document.fileName || 'Unnamed Document',
          documentType: document.type,
          url: document.url,
          certificateId: document.certificateId,
          vendorName: document.vendorName,
          flaggedAt,
          flaggedBy
        });
        saveFlaggedItems(state);
      }
    },
    
    unflagDocument: (state, action) => {
      const documentId = action.payload;
      state.documents = state.documents.filter(item => item.id !== documentId);
      saveFlaggedItems(state);
    },
    
    clearAllFlags: (state) => {
      state.certificates = [];
      state.documents = [];
      state.supplementalDocuments = [];
      saveFlaggedItems(state);
    },
    
    flagSupplementalDocument: (state, action) => {
      const { document, parentCertificateId, flaggedAt = new Date().toISOString(), flaggedBy } = action.payload;
      
      // Check if already flagged
      const existingIndex = state.supplementalDocuments.findIndex(item => item.id === document.id);
      if (existingIndex === -1) {
        state.supplementalDocuments.push({
          id: document.id,
          type: 'supplemental',
          name: document.name || document.fileName || 'Unnamed Supplemental Document',
          documentType: document.type || 'supplemental',
          url: document.url,
          parentCertificateId,
          certificateId: parentCertificateId,
          vendorName: document.vendorName,
          flaggedAt,
          flaggedBy
        });
        saveFlaggedItems(state);
      }
    },
    
    unflagSupplementalDocument: (state, action) => {
      const documentId = action.payload;
      state.supplementalDocuments = state.supplementalDocuments.filter(item => item.id !== documentId);
      saveFlaggedItems(state);
    },
    
    toggleCertificateFlag: (state, action) => {
      const { certificate, flaggedBy } = action.payload;
      const existingIndex = state.certificates.findIndex(item => item.id === certificate.id);
      
      if (existingIndex !== -1) {
        // Unflag if already flagged
        state.certificates = state.certificates.filter(item => item.id !== certificate.id);
      } else {
        // Flag if not flagged
        state.certificates.push({
          id: certificate.id,
          type: 'certificate',
          name: certificate.name || certificate.insuredName || 'Unnamed Certificate',
          insured: certificate.insured || certificate.insuredName,
          vendor: certificate.vendor,
          vendorName: certificate.vendorName,
          clientVendorID: certificate.clientVendorID,
          status: certificate.status,
          certificateDate: certificate.certificateDate,
          firstExp: certificate.firstExp,
          flaggedAt: new Date().toISOString(),
          flaggedBy
        });
      }
      saveFlaggedItems(state);
    },
    
    toggleDocumentFlag: (state, action) => {
      const { document, flaggedBy } = action.payload;
      const existingIndex = state.documents.findIndex(item => item.id === document.id);
      
      if (existingIndex !== -1) {
        // Unflag if already flagged
        state.documents = state.documents.filter(item => item.id !== document.id);
      } else {
        // Flag if not flagged
        state.documents.push({
          id: document.id,
          type: 'document',
          name: document.name || document.fileName || 'Unnamed Document',
          documentType: document.type,
          url: document.url,
          certificateId: document.certificateId,
          vendorName: document.vendorName,
          flaggedAt: new Date().toISOString(),
          flaggedBy
        });
      }
      saveFlaggedItems(state);
    },
    
    toggleSupplementalDocumentFlag: (state, action) => {
      const { document, parentCertificateId, flaggedBy } = action.payload;
      const existingIndex = state.supplementalDocuments.findIndex(item => item.id === document.id);
      
      if (existingIndex !== -1) {
        // Unflag if already flagged
        state.supplementalDocuments = state.supplementalDocuments.filter(item => item.id !== document.id);
      } else {
        // Flag if not flagged
        state.supplementalDocuments.push({
          id: document.id,
          type: 'supplemental',
          name: document.name || document.fileName || 'Unnamed Supplemental Document',
          documentType: document.type || 'supplemental',
          url: document.url,
          parentCertificateId,
          certificateId: parentCertificateId,
          vendorName: document.vendorName,
          flaggedAt: new Date().toISOString(),
          flaggedBy
        });
      }
      saveFlaggedItems(state);
    }
  }
});

export const {
  flagCertificate,
  unflagCertificate,
  flagDocument,
  unflagDocument,
  flagSupplementalDocument,
  unflagSupplementalDocument,
  clearAllFlags,
  toggleCertificateFlag,
  toggleDocumentFlag,
  toggleSupplementalDocumentFlag
} = flaggedItemsSlice.actions;

// Selectors
export const selectFlaggedCertificates = (state) => state.flaggedItems?.certificates || [];
export const selectFlaggedDocuments = (state) => state.flaggedItems?.documents || [];
export const selectFlaggedSupplementalDocuments = (state) => state.flaggedItems?.supplementalDocuments || [];
export const selectAllFlaggedItems = (state) => [
  ...(state.flaggedItems?.certificates || []),
  ...(state.flaggedItems?.documents || []),
  ...(state.flaggedItems?.supplementalDocuments || [])
];
export const selectIsCertificateFlagged = (certificateId) => (state) => 
  state.flaggedItems?.certificates?.some(item => item.id === certificateId) || false;
export const selectIsDocumentFlagged = (documentId) => (state) => 
  state.flaggedItems?.documents?.some(item => item.id === documentId) || false;
export const selectIsSupplementalDocumentFlagged = (documentId) => (state) => 
  state.flaggedItems?.supplementalDocuments?.some(item => item.id === documentId) || false;

export default flaggedItemsSlice.reducer;